export type GoalState = 'on_track' | 'achieved' | 'off_track';

export function getMultiGoalStatus(states: GoalState[]) {
  const total = states.length;
  const onTrackCount = states.filter((s) => s === 'on_track' || s === 'achieved').length;
  const achievedCount = states.filter((s) => s === 'achieved').length;

  if (total === 0) {
    return { label: 'Snapshot', color: 'gray' as const };
  }

  if (achievedCount === total) {
    return { label: 'Achieved', color: 'green' as const };
  }

  if (onTrackCount === total) {
    return { label: 'On track', color: 'green' as const };
  }

  if (onTrackCount === 0) {
    return { label: 'At risk', color: 'red' as const, tooltip: 'All goals are more than 10% behind goal.' };
  }

  return { label: 'Watch', color: 'yellow' as const, tooltip: 'Some goals are on track.' };
}

type PaceDirection = 'min' | 'max';

const parseQuarterLabel = (label: string): { year: number; quarter: number } | null => {
  const m1 = label.match(/^Q([1-4])\s+(\d{4})$/);
  if (m1) return { year: Number(m1[2]), quarter: Number(m1[1]) };
  const m2 = label.match(/^(\d{4})\s+Q([1-4])$/);
  if (m2) return { year: Number(m2[1]), quarter: Number(m2[2]) };
  return null;
};

export function getAnnualPaceStatus({
  ytd,
  goalPerYear,
  quarterLabel,
  direction = 'min',
  tolerance = 0.1,
}: {
  ytd: number;
  goalPerYear: number;
  quarterLabel: string;
  direction?: PaceDirection;
  tolerance?: number;
}) {
  const meta = parseQuarterLabel(quarterLabel);
  if (!meta || goalPerYear <= 0) return { label: 'Snapshot', color: 'gray' as const };

  const expected = (goalPerYear * meta.quarter) / 4;
  const margin = expected * tolerance;

  if (direction === 'min') {
    if (ytd >= goalPerYear) return { label: 'Achieved', color: 'green' as const };
    if (ytd >= expected) return { label: 'On track', color: 'green' as const };
    if (ytd >= expected - margin) return { label: 'Watch', color: 'yellow' as const };
    return { label: 'At risk', color: 'red' as const };
  }

  if (ytd <= goalPerYear) return { label: 'Achieved', color: 'green' as const };
  if (ytd <= expected) return { label: 'On track', color: 'green' as const };
  if (ytd <= expected + margin) return { label: 'Watch', color: 'yellow' as const };
  return { label: 'At risk', color: 'red' as const };
}

export function getAnnualPaceState({
  ytd,
  goalPerYear,
  quarterLabel,
  direction = 'min',
  tolerance = 0.1,
}: {
  ytd: number;
  goalPerYear: number;
  quarterLabel: string;
  direction?: PaceDirection;
  tolerance?: number;
}): GoalState {
  const meta = parseQuarterLabel(quarterLabel);
  if (!meta || goalPerYear <= 0) return 'off_track';

  const expected = (goalPerYear * meta.quarter) / 4;
  const margin = expected * tolerance;

  if (direction === 'min') {
    if (ytd >= goalPerYear) return 'achieved';
    if (ytd >= expected - margin) return 'on_track';
    return 'off_track';
  }

  if (ytd <= goalPerYear) return 'achieved';
  if (ytd <= expected + margin) return 'on_track';
  return 'off_track';
}

export function getThresholdGoalState({
  value,
  goal,
  direction = 'min',
  tolerance = 0.1,
}: {
  value: number | null;
  goal: number;
  direction?: PaceDirection;
  tolerance?: number;
}): GoalState {
  if (value === null) return 'off_track';
  const margin = goal * tolerance;

  if (direction === 'min') {
    if (value >= goal) return 'achieved';
    if (value >= goal - margin) return 'on_track';
    return 'off_track';
  }

  if (value <= goal) return 'achieved';
  if (value <= goal + margin) return 'on_track';
  return 'off_track';
}

export function getRangeGoalState({
  value,
  min,
  max,
  tolerance = 0.1,
}: {
  value: number | null;
  min: number;
  max: number;
  tolerance?: number;
}): GoalState {
  if (value === null) return 'off_track';
  const margin = min * tolerance;
  if (value >= max) return 'achieved';
  if (value >= min - margin) return 'on_track';
  return 'off_track';
}
